import type { ReactNode } from 'react';
import type { InteractiveAtomSize } from '../utils/InteractiveAtomProps';

export type FormFieldSize = InteractiveAtomSize;
export type FormFieldBaseProps = {
  className?: string;
  dataTestId?: string;
  description?: ReactNode;
  disabled?: boolean;
  error?: string;
  primaryHelperText?: ReactNode;
  secondaryHelperText?: ReactNode;
  htmlFor?: string;
  label?: ReactNode;
  labelTooltip?: ReactNode;
  link?: ReactNode;
  size?: FormFieldSize;
};

/**
 * Stable element ids that `FormField` derives from its `id` prop (or a fallback
 * `useId`) and exposes to a `renderControl` child. The child wires these into
 * `aria-labelledby` / `aria-describedby` on the actual control. Each id is
 * `undefined` when the corresponding chrome element is not rendered, so the
 * child can do `[ids.descriptionId, ids.helperId, ids.errorId].filter(Boolean)`.
 */
export type FormFieldIds = {
  /** Id of the form control inside the field (label's `htmlFor` target). */
  baseId: string;
  /** Id of the rendered Label, or undefined when no label is shown. */
  labelId: string | undefined;
  /** Id of the rendered description span, or undefined when not shown. */
  descriptionId: string | undefined;
  /** Id of the helper-text container, or undefined when no helper is shown. */
  helperId: string | undefined;
  /** Id of the error span, or undefined when no error is shown. */
  errorId: string | undefined;
};

/**
 * Legacy mode: caller passes `children` for the inner control and (optionally)
 * `htmlFor` so the rendered Label wires to it. FormField emits no chrome ids
 * — the child wires its own labelling.
 *
 * `renderControl` is forbidden here so the two modes never silently collide.
 */
export type FormFieldChildrenModeProps = FormFieldBaseProps & {
  children?: ReactNode;
  renderControl?: never;
};

/**
 * Render-control mode: caller passes a `renderControl` callback and FormField
 * supplies stable chrome ids the child must wire into the inner control's
 * `aria-labelledby` / `aria-describedby`. `id` / `onLabelClick` are part of
 * this mode's contract; `htmlFor` from the base is unused (Label uses the
 * derived `baseId`).
 *
 * `children` is forbidden here so the two modes never silently collide.
 */
export type FormFieldRenderControlModeProps = FormFieldBaseProps & {
  renderControl: (ids: FormFieldIds) => ReactNode;
  /**
   * Base id used both as the Label's `htmlFor` target and as the prefix for
   * derived chrome ids passed to `renderControl`. Falls back to `useId` when
   * omitted.
   */
  id?: string;
  /**
   * Optional click handler forwarded to the Label. Useful for non-native
   * controls (e.g. contenteditable) where `<label htmlFor>` does not auto-focus
   * and the field needs to invoke a focus method on a ref.
   */
  onLabelClick?: () => void;
  children?: never;
};

export type FormFieldProps =
  | FormFieldChildrenModeProps
  | FormFieldRenderControlModeProps;

// base props type for toggle form fields (e.g. CheckboxField, RadioField, SwitchField)
export type FormFieldToggleBaseProps = Omit<
  FormFieldBaseProps,
  'primaryHelperText' | 'secondaryHelperText'
> & {
  helperText?: ReactNode;
};
